import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const jwtSchema = z
  .string()
  .min(1)
  .regex(/^[\w-]+\.[\w-]+\.[\w-]+$/, "Invalid JWT format");

// ── Payload Types ─────────────────────────────────────────
export interface ContentPostEntry {
  id: string;
  user_id: string;
  title: string;
  content: string | null;
  image_url: string | null;
  created_at: string;
  updated_at: string;
  author_name: string;
  author_username: string;
  author_avatar: string | null;
}

export interface ContentListPayload {
  posts: ContentPostEntry[];
  total: number;
}

// ── Server Functions ──────────────────────────────────────
export const getContentPosts = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
      page: z.number().int().min(1).default(1),
      limit: z.number().int().min(1).max(100).default(10),
      search: z.string().optional(),
    })
  )
  .handler(async ({ data }) => {
    const { createAdminClient } = await import("./_admin-utils.server");
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError ?? "Missing server env vars" };

    const { data: { user }, error: authError } = await client.auth.getUser(data.accessToken);
    if (authError || !user) return { data: null, error: "Unauthorized" };

    const offset = (data.page - 1) * data.limit;

    let query = client
      .from("posts")
      .select("id, user_id, title, content, image_url, created_at, updated_at", { count: "exact" })
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .range(offset, offset + data.limit - 1);

    if (data.search) {
      const safeSearch = data.search.replace(/[%_]/g, "\\$&");
      query = query.ilike("title", `%${safeSearch}%`);
    }

    const { data: posts, error, count } = await query;

    if (error) return { data: null, error: error.message };

    const { data: profile } = await client
      .from("profiles")
      .select("full_name, username, avatar_url")
      .eq("id", user.id)
      .maybeSingle();

    const authorName = profile?.full_name ?? user.user_metadata?.full_name ?? "";
    const authorUsername = profile?.username ?? user.email?.split("@")[0] ?? "";
    const authorAvatar = profile?.avatar_url ?? user.user_metadata?.avatar_url ?? null;

    const entries: ContentPostEntry[] = (posts ?? []).map((p) => ({
      id: String(p.id),
      user_id: String(p.user_id),
      title: String(p.title ?? ""),
      content: p.content ? String(p.content) : null,
      image_url: p.image_url ? String(p.image_url) : null,
      created_at: String(p.created_at),
      updated_at: String(p.updated_at),
      author_name: authorName,
      author_username: authorUsername,
      author_avatar: authorAvatar,
    }));

    return { data: { posts: entries, total: count ?? 0 }, error: null };
  });

export const deleteContentPost = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
      postId: z.string().uuid(),
    })
  )
  .handler(async ({ data }) => {
    const { createAdminClient } = await import("./_admin-utils.server");
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError ?? "Missing server env vars" };

    const { data: { user }, error: authError } = await client.auth.getUser(data.accessToken);
    if (authError || !user) return { data: null, error: "Unauthorized" };

    const { error } = await client
      .from("posts")
      .delete()
      .eq("id", data.postId)
      .eq("user_id", user.id);

    if (error) return { data: null, error: error.message };

    // Fire-and-forget: broadcast content post deletion to Mycelium event bus
    const { publishMyceliumEvent } = await import("./_mycelium.server");
    await publishMyceliumEvent(client, {
      domain: "documents",
      type: "content_post_deleted",
      userId: user.id,
      payload: { postId: data.postId },
    });

    return { data: { deleted: true }, error: null };
  });
